import React, { useEffect, useState } from 'react';
import MUIDataTable from 'mui-datatables';
import moment from 'moment';
// GraphQl
import { useLazyQuery, useQuery, useMutation } from '@apollo/react-hooks';

// Components & Others
import LoadingIndicator from '../../../LoadingIndicator';
import {
  LIST_PARTICIPANT_RAISING,
  APPROVE_VINCULATED_PARTICIPANT,
  DISAPPROVE_VINCULATED_PARTICIPANT,
  FILE_DOWNLOAD,
} from '../queries';
import {
  generateColumn,
  edgeToList,
  getOptions,
  decimalAdjust,
  successMessage,
  errorMessage,
} from '../../../../utils/commonFunctions';
import { TableCell, Typography, Tooltip, IconButton } from '@material-ui/core';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { useSnackbar } from 'notistack';
import NumberFormat from 'react-number-format';
import CustomOptions from '../../../CustomOptions';
import { textLabels } from '../../../../translations/components/MUIDataTable';
import {
  LINKED_TYPES,
  POSITION_CHOICES,
  COMPANY_CHOICES,
} from '../../../Cognito/StepThreeForm/LinkedPeople/constants';
import PDFViewerModal from '../pdfViewer';

// styles and icons
import useStyles from './styles';

function VinculatedList() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [participationList, setParticipationList] = useState([]);
  const [relativeList, setRelativeList] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [isEmployee, setIsEmployee] = useState(false);
  const [urls, setUrls] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const [approveComplianceClient] = useMutation(
    APPROVE_VINCULATED_PARTICIPANT,
    getOptions({
      mutationName: 'approveVinculatedParticipant',
      message: 'Orden Aprobada',
      modelName: 'approved',
      completeCallback: () => getParticipants(),
      enqueueSnackbar,
      refetchQueries: [{ query: LIST_PARTICIPANT_RAISING }],
    }),
  );

  const [disapproveComplianceClient] = useMutation(
    DISAPPROVE_VINCULATED_PARTICIPANT,
    getOptions({
      mutationName: 'disapproveVinculatedParticipant',
      message: 'Orden Desaprobada',
      modelName: 'disapproved',
      completeCallback: () => getParticipants(),
      enqueueSnackbar,
      refetchQueries: [{ query: LIST_PARTICIPANT_RAISING }],
    }),
  );

  const [getParticipants, { error, loading }] = useLazyQuery(
    LIST_PARTICIPANT_RAISING,
    {
      fetchPolicy: 'no-cache',
      onCompleted: dataList => {
        if (dataList) {
          const participationListTmp = edgeToList(
            dataList,
            'listNotValidatedParticipantRaising',
          )
            .filter(({ lastParticipation }) => !!lastParticipation)
            .map(participation => {
              const {
                id,
                lastParticipation,
                createdDate,
                numFee,
                amount,
                client,
                raising,
                classFund,
                validated,
                hasSignedSd,
              } = participation;
              setCurrencySymbol(raising.fund.currency.symbol);
              const newParticipation = {
                options: {
                  id: id,
                  state: lastParticipation.state,
                  validated: validated,
                  participantID: client.id,
                  hasSignedSd: hasSignedSd,
                },
                fund: raising.fund.businessName,
                order: raising.acronym,
                clientID: client.id,
                state: lastParticipation.state,
                dateEntry: createdDate,
                quotes: numFee,
                validated: validated,
                classFund: classFund ? classFund.name : '',
                amount: decimalAdjust('ceil', amount, -2),
                participants: [],
                percentajes: [],
                linkedPeoples: [],
              };
              newParticipation.participants.push(client.user);
              newParticipation.percentajes.push(
                lastParticipation.participationPercentage,
              );
              edgeToList(participation, 'linkedPeople').forEach(item => {
                newParticipation.linkedPeoples.push({
                  name:
                    item.linkedPeople.firstName +
                    ' ' +
                    item.linkedPeople.lastName,
                  document: item.linkedPeople.documentNumber,
                  company: COMPANY_CHOICES.find(
                    element => element.value == item?.linkedPeople.company,
                  )?.label,
                  position: POSITION_CHOICES.find(
                    element => element.value == item?.linkedPeople.position,
                  )?.label,
                  linkedType: LINKED_TYPES.find(
                    element => element.value == item?.linkedType,
                  )?.label,
                });
              });
              edgeToList(lastParticipation, 'coParticipants').forEach(
                ({ client: subClient, participationPercentage }) => {
                  if (subClient?.linkedpeopleclientrelationSet?.edges) {
                    subClient.linkedpeopleclientrelationSet.edges.forEach(
                      item => {
                        newParticipation.linkedPeoples.push({
                          name:
                            item.node.linkedPeople.firstName +
                            ' ' +
                            item.node.linkedPeople.lastName,
                          document: item.node.linkedPeople.documentNumber,
                          company: COMPANY_CHOICES.find(
                            element =>
                              element.value == item?.node.linkedPeople.company,
                          )?.label,
                          position: POSITION_CHOICES.find(
                            element =>
                              element.value == item?.node.linkedPeople.position,
                          )?.label,
                          linkedType: LINKED_TYPES.find(
                            element =>
                              element.value ==
                              item?.node.linkedPeople.linkedType,
                          )?.label,
                        });
                      },
                    );
                  }
                  newParticipation.participants.push(subClient.user);
                  newParticipation.percentajes.push(participationPercentage);
                },
              );
              return newParticipation;
            });
          setParticipationList(participationListTmp);
        }
      },
    },
  );

  if (error) {
    console.error('list clients', error);
  }

  useEffect(() => {
    getParticipants();
  }, []);

  const [downloadFile, { loadingFile, dataFile }] = useMutation(FILE_DOWNLOAD, {
    onError(error) {
      errorMessage(error, enqueueSnackbar);
    },
    onCompleted(data) {
      if (data?.fileDownloadSd?.urls?.length < 1) {
        errorMessage('No existen archivos', enqueueSnackbar);
      } else {
        setUrls(data?.fileDownloadSd?.urls);
        setShowPreview(true);
        successMessage('Archivos descargados.', enqueueSnackbar);
      }
    },
  });

  const columnOptions = {
    filter: false,
    sort: true,
  };

  const columns = [
    generateColumn('Fondo', 'fund', {
      customBodyRender: value => <span>{value}</span>,
    }),
    generateColumn('Levantamiento', 'order', {
      customBodyRender: value => <span>{value}</span>,
    }),
    generateColumn('Clase', 'classFund'),
    generateColumn('Nro. Documento', 'participants', {
      ...columnOptions,
      customBodyRender: participants => (
        <div className={classes.multilineCell}>
          {participants.map(({ documentNumber, isVinculated }) =>
            isVinculated ? (
              <span>
                <b>{`${documentNumber}`}</b> |{' '}
              </span>
            ) : (
              <span>{`${documentNumber}`}</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Participante', 'participants', {
      ...columnOptions,
      customBodyRender: participants => (
        <div className={classes.multilineCell}>
          {participants.map(
            ({ firstName, lastName, maternalSurname, isVinculated }) =>
              isVinculated ? (
                <span>
                  <b>{`${firstName} ${lastName} ${maternalSurname}`}</b> |{' '}
                </span>
              ) : (
                <span>{`${firstName} ${lastName} ${maternalSurname}`}</span>
              ),
          )}
        </div>
      ),
    }),
    generateColumn('Cuotas', 'quotes'),
    generateColumn('Monto', 'amount', {
      ...columnOptions,
      customBodyRender: amount => (
        <>
          {`${currencySymbol} `}
          <NumberFormat
            displayType="text"
            fixedDecimalScale
            decimalScale={2}
            thousandSeparator
            value={amount}
          />
        </>
      ),
    }),
    generateColumn('Fecha', 'dateEntry', {
      ...columnOptions,
      customBodyRender: value => moment(value).format('YYYY-MM-DD HH:mm:ss'),
    }),
    generateColumn('Porcentaje', 'percentajes', {
      ...columnOptions,
      customBodyRender: percentajes => (
        <div className={classes.multilineCell}>
          {percentajes.map((percentaje, i) =>
            i > 0 ? (
              <span> | {percentaje}% |</span>
            ) : (
              <span>{percentaje}%</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Vinculado', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ name }) =>
            name ? (
              <span>
                {` ${name}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Vinculación', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ linkedType }) =>
            linkedType ? (
              <span>
                {` ${linkedType}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Documento', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ document }) =>
            document ? (
              <span>
                {` ${document}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Empresa', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ company }) =>
            company ? (
              <span>
                {` ${company}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Cargo', 'linkedPeoples', {
      ...columnOptions,
      customBodyRender: linkedPeoples => (
        <div className={classes.multilineCell}>
          {linkedPeoples.map(({ position }) =>
            position ? (
              <span>
                {` ${position}`}
                <b>{' |'}</b>
              </span>
            ) : (
              <span>-</span>
            ),
          )}
        </div>
      ),
    }),
    generateColumn('Opciones', 'options', {
      ...columnOptions,
      customBodyRender: value => {
        const { id, hasSignedSd } = value;
        let options = ['oconfirm', 'odeny'];
        if (hasSignedSd) {
          options.push('ocustom');
        }
        return (
          <CustomOptions
            id={id}
            model="Orden"
            options={options}
            handleConfirmOption={() =>
              approveComplianceClient({ variables: { orderId: id } })
            }
            handleDenyOption={() =>
              disapproveComplianceClient({ variables: { orderId: id } })
            }
            customOption={
              <>
                <Tooltip title="Descargar DJ" placement="top" arrow>
                  <IconButton
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={$ev => {
                      $ev.stopPropagation();
                      downloadFile({
                        variables: { orderId: id },
                      });
                    }}
                    className={classes.optionButtons}
                  >
                    <CloudDownloadIcon className={classes.optionIcon} />
                  </IconButton>
                </Tooltip>
              </>
            }
          />
        );
      },
      customHeadRender: columnMeta => {
        return (
          <TableCell
            id={columnMeta.index}
            key={columnMeta.index}
            style={{ borderBottom: 'none' }}
          >
            <Typography
              variant="subtitle1"
              align="left"
              className={classes.tableTitle}
            >
              {columnMeta.label}
            </Typography>
          </TableCell>
        );
      },
    }),
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    selectableRows: 'none',
    print: false,
    filter: true,
    download: false,
    textLabels,
  };

  return loading ? (
    <LoadingIndicator />
  ) : (
    <>
      <MUIDataTable
        title="Aprobación Vinculados"
        data={participationList}
        columns={columns}
        options={options}
      />
      <PDFViewerModal
        showModal={showPreview}
        onClosePress={() => setShowPreview(false)}
        fileURLs={urls}
        loading={loading}
      />
    </>
  );
}

export default VinculatedList;
